import { createNgModuleRef, Injectable, Injector, NgModuleRef, Inject } from '@angular/core';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import each from 'lodash-es/each';
import { BehaviorSubject } from 'rxjs';
import { App } from '@df/core/app';
import { Acl } from '@df/core/service/acl';
import { Client } from '@df/core/service/client';

@Injectable({ providedIn: 'root' })
export class ICmsPluginLoaderService {
    /**
     * Module refs
     */
    private adminModules: { [key: string]: NgModuleRef<any> } = {};
    private clientModules: { [key: string]: NgModuleRef<any> } = {};

    /**
     * If user may have access to CMS
     */
    readonly hasPotentialCmsAccess$ = new BehaviorSubject<boolean>(false);

    /**
     * Dependencies
     */
    constructor(
        @Inject(ICMS_CLIENT_CONFIG) private config: NICmsClientConfig.IConfig,
        private app: App,
        acl: Acl,
        client: Client
    ) {
        acl.authStatus.subscribe(() => {
            const hasPotentialCmsAccess = app.isPlatformBrowser && (client.isCms$.value || acl.check('cms')); // || !acl.isProduction || acl.isDeveloper);
            if (this.hasPotentialCmsAccess$.value !== hasPotentialCmsAccess) {
                this.hasPotentialCmsAccess$.next(hasPotentialCmsAccess);
            }
        });
    }

    private editorExpectedState$!: BehaviorSubject<boolean>;

    init(editorExpectedState$: BehaviorSubject<boolean>) {
        this.editorExpectedState$ = editorExpectedState$;
    }

    loadClientModules(clientInjector: Injector) {
        if (this.config.modules?.campaigns?.enabled) {
            this.loadCampaignsClientModule(clientInjector);
        }
        if (this.config.modules?.merchandiser?.enabled) {
            this.loadMerchandiserClientModule(clientInjector);
        }
        if (this.config.modules?.markerIo?.enabled) {
            this.loadMarkerIoClientModule(clientInjector);
        }

        const shortcutsSubscriptipn = this.hasPotentialCmsAccess$.subscribe(hasAccess => {
            if (hasAccess) {
                this.loadShortcutsClientModule(clientInjector);
                setTimeout(() => {
                    shortcutsSubscriptipn.unsubscribe();
                });
            }
        });
    }

    loadAdminModules(adminInjector: Injector) {
        const promises: Promise<void>[] = [];

        if (this.config.modules?.campaigns?.enabled) {
            promises.push(this.loadCampaignsAdminModule(adminInjector));
        }

        if (this.config.modules?.merchandiser?.enabled) {
            promises.push(this.loadMerchandiserAdminModule(adminInjector));
        }

        return Promise.all(promises);
    }

    destroyAdminModules() {
        each(this.adminModules, moduleRef => moduleRef.destroy());
        this.adminModules = {};
    }

    /**
     * Loads editor module
     */
    private async loadCampaignsAdminModule(injector: Injector) {
        this.createAdminModule(
            injector,
            'campaings-admin',
            (await import(/* webpackChunkName: "icms-campaigns-admin.aw" */ './campaigns/icms-campaigns-admin.module'))
                .ICmsCampaignsAdminModule
        );
    }

    /**
     * Loads editor module
     */
    private async loadCampaignsClientModule(injector: Injector) {
        this.createClientModule(
            injector,
            'campaings-client',
            (await import(/* webpackChunkName: "icms-campaigns-client.aw" */ './campaigns/client/icms-campaigns-client.module'))
                .ICmsCampaignsClientModule
        );
    }

    /**
     * Loads merchandiser module
     */
    private async loadMerchandiserAdminModule(injector: Injector) {
        this.createAdminModule(
            injector,
            'merchandiser-admin',
            (await import(/* webpackChunkName: "icms-merchandiser-admin.aw" */ './merchandiser/icms-merchandiser-admin.module'))
                .ICmsMerchandiserAdminModule
        );
    }

    /**
     * Loads editor module
     */
    private async loadMerchandiserClientModule(injector: Injector) {
        this.createClientModule(
            injector,
            'merchandiser-client',
            (await import(/* webpackChunkName: "icms-merchandiser-client.aw" */ './merchandiser/client/icms-merchandiser-client.module'))
                .ICmsMerchandiserClientModule
        );
    }
    /**
     * Loads client command center module
     */
    private async loadShortcutsClientModule(clientInjector: Injector) {
        this.createClientModule(
            clientInjector,
            'shortcuts-client',
            (await import(/* webpackChunkName: "icms-shortcuts-client.aw" */ './shortcuts/icms-shortcuts.module')).ICmsShortcutsModule
        );
    }

    private async loadMarkerIoClientModule(injector: Injector) {
        this.createClientModule(
            injector,
            'markerio-client',
            (await import(/* webpackChunkName: "icms-markerio-client.aw" */ './marker-io/marker-io.module')).MarkerIOModule
        );
    }

    private createAdminModule(injector: Injector, id: string, module: any) {
        if (!this.adminModules[id] && this.editorExpectedState$.value) {
            this.adminModules[id] = createNgModuleRef(module, injector);
        }
    }
    private createClientModule(injector: Injector, id: string, module: any) {
        if (!this.clientModules[id]) {
            this.clientModules[id] = createNgModuleRef(module, injector);
        }
    }
}
